import React, { useState, useEffect, useRef } from "react";
import * as FaIcons from "react-icons/fa";
import { apiUrl } from "../ApiManager/AxiosHeaders";
import Paginations from "../GenericComponents/Pagination";
import ExpiringProvidersFilterBar from "./ExpiringProvidersFilterBar";
import "../Clients/clients.css";
import "../reports/commissions/commissions-responsive.css";
import "../../styles/tailwind.css";

const CATEGORY_OPTIONS = [
  { label: "All", value: "" },
  { label: "Education", value: "Education" },
  { label: "Migration", value: "Migration" },
  {
    label: "Administrative Review Tribunal",
    value: "Administrative Review Tribunal",
  },
  {
    label: "Skills Assessment Application",
    value: "Skills Assessment Application",
  },
  { label: "Service Provider", value: "Service Provider" },
];

const EXPIRY_OPTIONS = [
  { label: "All Providers", value: "ALL" },
  { label: "Expiring in 7 Days", value: "7_DAYS" },
  { label: "Expiring in 1 Month", value: "1_MONTH" },
  { label: "Expiring in 2 Months", value: "2_MONTHS" },
  { label: "Expiring in 3 Months", value: "3_MONTHS" },
  { label: "Expired Providers", value: "EXPIRED" },
];

const PROVIDER_SEARCH_DEBOUNCE_MS = 400;

function getStatusBadgeClass(text = "") {
  if (text.includes("Expired")) return "bg-red-100 text-red-800";
  if (text.includes("7 Days")) return "bg-orange-100 text-orange-800";
  if (
    text.includes("1 Month") ||
    text.includes("2 Months") ||
    text.includes("3 Months")
  ) {
    return "bg-yellow-100 text-yellow-800";
  }
  if (text.includes("Not Confirmed") || text.includes("No Expiry")) {
    return "bg-gray-200 text-gray-700";
  }
  if (text.startsWith("Expires in")) return "bg-green-100 text-green-800";
  return "bg-gray-100 text-gray-800";
}

function formatDate(value) {
  if (!value) return "—";
  return new Date(value).toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function ExpiringProviders() {
  const [selectedCategory, setSelectedCategory] = useState("");
  const [selectedExpiry, setSelectedExpiry] = useState("ALL");
  const [providerNameDraft, setProviderNameDraft] = useState("");
  const [appliedProviderName, setAppliedProviderName] = useState("");
  const [skeleton, setSkeleton] = useState(false);
  const [data, setData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [lastPage, setLastPage] = useState(1);
  const skipProviderDebounceRef = useRef(false);
  const providerDebounceReadyRef = useRef(false);
  const fetchSeqRef = useRef(0);

  useEffect(() => {
    const seq = ++fetchSeqRef.current;
    let cancelled = false;

    const load = async () => {
      setSkeleton(true);
      try {
        const params = {
          page: currentPage,
          perPage,
          category: selectedCategory,
          providerName: appliedProviderName.trim(),
          expiry: selectedExpiry || "ALL",
        };

        const response = await apiUrl.get("/provider/expirey", { params });

        if (cancelled || seq !== fetchSeqRef.current) return;

        if (response?.data?.status) {
          setData(response.data.data || []);
          const pagination = response.data.pagination || {};
          setTotalItems(pagination.total || 0);
          setLastPage(pagination.lastPage || 1);
          const serverPage = pagination.currentPage;
          if (serverPage && serverPage !== currentPage) {
            setCurrentPage(serverPage);
          }
        } else {
          setData([]);
          setTotalItems(0);
          setLastPage(1);
        }
      } catch (error) {
        if (!cancelled && seq === fetchSeqRef.current) {
          console.error(error);
          setData([]);
          setTotalItems(0);
          setLastPage(1);
        }
      } finally {
        if (!cancelled && seq === fetchSeqRef.current) {
          setSkeleton(false);
        }
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [
    currentPage,
    perPage,
    selectedCategory,
    appliedProviderName,
    selectedExpiry,
  ]);

  useEffect(() => {
    if (skipProviderDebounceRef.current) {
      skipProviderDebounceRef.current = false;
      return;
    }

    const timer = setTimeout(() => {
      if (!providerDebounceReadyRef.current && providerNameDraft === "") {
        providerDebounceReadyRef.current = true;
        return;
      }
      providerDebounceReadyRef.current = true;
      setAppliedProviderName((prev) => {
        if (prev === providerNameDraft) return prev;
        setCurrentPage(1);
        return providerNameDraft;
      });
    }, PROVIDER_SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [providerNameDraft]);

  const handleCategoryChange = (value) => {
    setSelectedCategory(value);
    setCurrentPage(1);
  };

  const handleExpiryChange = (value) => {
    setSelectedExpiry(value);
    setCurrentPage(1);
  };

  const handleClearCategory = () => {
    setSelectedCategory("");
    setCurrentPage(1);
  };

  const handleClearExpiry = () => {
    setSelectedExpiry("ALL");
    setCurrentPage(1);
  };

  const handleClearProvider = () => {
    skipProviderDebounceRef.current = true;
    setProviderNameDraft("");
    setAppliedProviderName("");
    setCurrentPage(1);
  };

  const handleResetAll = () => {
    skipProviderDebounceRef.current = true;
    setSelectedCategory("");
    setSelectedExpiry("ALL");
    setProviderNameDraft("");
    setAppliedProviderName("");
    setCurrentPage(1);
  };

  const handlePaginationCallback = (page, newPerPage) => {
    if (newPerPage !== undefined && newPerPage !== null && newPerPage !== "") {
      const parsed = parseInt(newPerPage, 10);
      const nextPerPage =
        parsed >= totalItems && totalItems > 0 ? totalItems : parsed;
      setPerPage(nextPerPage);
      setCurrentPage(1);
      return;
    }

    setCurrentPage(page);
  };

  const hasActiveFilters =
    Boolean(selectedCategory) ||
    selectedExpiry !== "ALL" ||
    Boolean(appliedProviderName.trim());

  return (
    <div className="client-invoices-xero px-3 sm:px-4 lg:px-5">
      <div className="client-invoices-xero__breadcrumb">
        <span>Manage Providers</span>
        <span aria-hidden="true">›</span>
        <span>Expiring Providers</span>
      </div>

      <div className="client-invoices-xero__page-head ep-expiry-page-head">
        <div className="ep-expiry-page-head__main">
          <h1 className="client-invoices-xero__title">Provider Expiry Timeline</h1>
          <p className="client-invoices-xero__subtitle">
            Track provider agreements by category, expiry range, and name.
          </p>
        </div>
        {!skeleton && totalItems > 0 ? (
          <span className="ep-expiry-page-head__count">
            <span className="ep-expiry-page-head__count-dot" aria-hidden />
            {totalItems.toLocaleString()} result{totalItems === 1 ? "" : "s"}
          </span>
        ) : skeleton ? (
          <span
            className="ep-expiry-page-head__count-skeleton"
            aria-label="Loading results count"
          />
        ) : null}
      </div>

      <div className="tailwind-container pb-2">
        <ExpiringProvidersFilterBar
          categoryOptions={CATEGORY_OPTIONS}
          expiryOptions={EXPIRY_OPTIONS}
          selectedCategory={selectedCategory}
          selectedExpiry={selectedExpiry}
          providerNameDraft={providerNameDraft}
          appliedProviderName={appliedProviderName}
          onCategoryChange={handleCategoryChange}
          onExpiryChange={handleExpiryChange}
          onProviderNameChange={setProviderNameDraft}
          onClearCategory={handleClearCategory}
          onClearExpiry={handleClearExpiry}
          onClearProvider={handleClearProvider}
          onResetAll={handleResetAll}
        />

        <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[760px] border-collapse">
              <thead>
                <tr className="bg-gray-50">
                  <th className="px-4 py-3.5 text-left text-xs font-bold uppercase tracking-wide text-gray-500">
                    Provider Name
                  </th>
                  <th className="px-4 py-3.5 text-left text-xs font-bold uppercase tracking-wide text-gray-500">
                    Category
                  </th>
                  <th className="px-4 py-3.5 text-left text-xs font-bold uppercase tracking-wide text-gray-500">
                    Campus
                  </th>
                  <th className="px-4 py-3.5 text-left text-xs font-bold uppercase tracking-wide text-gray-500">
                    Start Date
                  </th>
                  <th className="px-4 py-3.5 text-left text-xs font-bold uppercase tracking-wide text-gray-500">
                    End Date
                  </th>
                  <th className="px-4 py-3.5 text-left text-xs font-bold uppercase tracking-wide text-gray-500">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody>
                {skeleton ? (
                  [...Array(5)].map((_, rowIndex) => (
                    <tr key={rowIndex} className="border-t border-gray-100">
                      {[...Array(6)].map((__, cellIndex) => (
                        <td key={cellIndex} className="px-4 py-4">
                          <div
                            className="h-4 animate-pulse rounded bg-gray-200"
                            style={{ width: "55%" }}
                          />
                        </td>
                      ))}
                    </tr>
                  ))
                ) : data.length === 0 ? (
                  <tr>
                    <td colSpan={6}>
                      <div className="flex flex-col items-center justify-center px-6 py-14 text-center">
                        <FaIcons.FaCalendarTimes
                          size={40}
                          className="text-gray-300"
                        />
                        <h6 className="mt-3 mb-1 font-semibold text-gray-600">
                          No providers found
                        </h6>
                        <p className="mb-0 text-sm text-gray-400">
                          {hasActiveFilters
                            ? "Try adjusting your filters or search term."
                            : "No expiring providers match the current criteria."}
                        </p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  data.map((provider) => {
                    const statusText = provider.status || "Active";

                    return (
                      <tr
                        key={provider.providerid}
                        className="border-t border-gray-100 transition hover:bg-blue-50/40"
                      >
                        <td className="px-4 py-3.5 text-sm font-semibold text-gray-900">
                          {provider.providername || "—"}
                        </td>
                        <td className="px-4 py-3.5 text-sm text-gray-600">
                          {provider.category || "—"}
                        </td>
                        <td className="px-4 py-3.5 text-sm text-gray-600">
                          {provider.campus || "—"}
                        </td>
                        <td className="px-4 py-3.5 text-sm text-gray-600">
                          {formatDate(provider.agreementstartdate)}
                        </td>
                        <td className="px-4 py-3.5 text-sm text-gray-600">
                          {formatDate(provider.agreementenddate)}
                        </td>
                        <td className="px-4 py-3.5">
                          <span
                            className={`inline-block whitespace-nowrap rounded-full px-2.5 py-1 text-xs font-medium ${getStatusBadgeClass(statusText)}`}
                          >
                            {statusText}
                          </span>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>

        {!skeleton && data.length > 0 ? (
          <div className="mt-4">
            <Paginations
              currentPage={currentPage}
              totalItems={totalItems}
              itemsPerPage={perPage}
              lastPage={lastPage}
              perItem={data.length}
              onPageChange={handlePaginationCallback}
            />
          </div>
        ) : null}
      </div>
    </div>
  );
}

export default ExpiringProviders;
